/**
 * Le lien de dépôt public (A5, ARTO-129) : ce que la page `/depot/:jeton`
 * demande à l'API, et le transport de l'envoi par morceaux que
 * `@artosera/shared` ordonnance.
 *
 * Les routes publiques ne rendent aucun fichier, pas même la liste des
 * morceaux reçus. La page tire une SESSION d'envoi — ses fichiers sont rangés
 * à part de ceux des autres sessions — et chaque ouverture lui rend un SECRET
 * d'envoi, exigé par chaque geste suivant. Elle note, dans ce navigateur, la
 * session, le secret de chaque envoi et ses morceaux partis : après une
 * coupure ou un rechargement, rouvrir le même chemin à la même taille, secret
 * à l'appui, rend le même envoi, et la note dit ce qui n'a pas à repartir.
 * Sans note, une nouvelle session repart de zéro, sans rien heurter.
 */
import {
  ErreurEnvoiDepot,
  type ModeEnvoiDepot,
  type OuvertureFichierDepot,
  type TransportDepot,
} from '@artosera/shared';
import { envoyerMorceauDepotPublic, requeteDepotPublic } from './client.js';
import { ApiError } from './errors.js';

export interface AccueilDepot {
  octetsRestants: number;
  expireLe: string;
  tailleMorceau: number;
  envoisParalleles: number;
  mode: ModeEnvoiDepot;
}

export async function lireAccueilDepot(jeton: string, signal?: AbortSignal): Promise<AccueilDepot> {
  return (await requeteDepotPublic<AccueilDepot>(jeton, '', signal === undefined ? {} : { signal }))
    .donnees;
}

// ── Note locale : session, secrets, morceaux partis ──────────────────────

interface EnvoiNote {
  id: string;
  secret: string;
  recus: number[];
}

interface Note {
  session: string;
  /** Par fichier : chemin et taille. */
  envois: Record<string, EnvoiNote>;
}

/** La note d'un lien ; la clé ne porte pas le jeton, seulement son empreinte. */
async function cleNote(jeton: string): Promise<string> {
  const empreinte = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(jeton));
  const hex = Array.from(new Uint8Array(empreinte), (octet) => octet.toString(16).padStart(2, '0'));
  return `artosera-depot-${hex.join('').slice(0, 32)}`;
}

/** 32 octets aléatoires en base64url : une session d'envoi. */
function nouvelleSession(): string {
  const octets = crypto.getRandomValues(new Uint8Array(32));
  return btoa(String.fromCharCode(...octets))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function lireNote(cle: string): Note {
  try {
    const lue = JSON.parse(window.localStorage.getItem(cle) ?? 'null') as Partial<Note> | null;
    if (lue && typeof lue.session === 'string' && typeof lue.envois === 'object' && lue.envois)
      return { session: lue.session, envois: lue.envois };
  } catch {
    // Note illisible : une session neuve.
  }
  return { session: nouvelleSession(), envois: {} };
}

function ecrireNote(cle: string, note: Note): void {
  try {
    window.localStorage.setItem(cle, JSON.stringify(note));
  } catch {
    // Stockage indisponible (navigation privée) : la reprise repartira de zéro.
  }
}

/** Une erreur d'API devient une erreur d'envoi : un refus ne se réessaie pas. */
function erreurEnvoi(erreur: unknown): unknown {
  if (!(erreur instanceof ApiError)) return erreur;
  const raison = (erreur.details as { raison?: unknown } | null)?.raison;
  const passagere = erreur.status >= 500 || erreur.status === 408 || erreur.status === 429;
  return new ErreurEnvoiDepot(
    erreur.message,
    !passagere,
    typeof raison === 'string' ? raison : erreur.code,
  );
}

async function relayer<T>(geste: () => Promise<T>): Promise<T> {
  try {
    return await geste();
  } catch (erreur) {
    throw erreurEnvoi(erreur);
  }
}

/** Le transport d'un lien de dépôt public. */
export async function transportDepotPublic(
  jeton: string,
  signal: AbortSignal,
): Promise<TransportDepot<Blob>> {
  const cle = await cleNote(jeton);
  const note = lireNote(cle);
  ecrireNote(cle, note);
  const parId = new Map<string, string>();
  for (const [nom, envoi] of Object.entries(note.envois)) parId.set(envoi.id, nom);
  const envoiDe = (fichierId: string): EnvoiNote => {
    const envoi = note.envois[parId.get(fichierId) ?? ''];
    if (envoi === undefined) throw new ErreurEnvoiDepot('Envoi inconnu de cette page.', true);
    return envoi;
  };

  return {
    ouvrir: (chemin, octets) =>
      relayer(async (): Promise<OuvertureFichierDepot> => {
        const nom = `${chemin}\u0000${String(octets)}`;
        const connu = note.envois[nom];
        const { donnees } = await requeteDepotPublic<{
          id: string;
          reprise: boolean;
          secret?: string;
          envoi: { mode: ModeEnvoiDepot } | null;
        }>(jeton, '/fichiers', {
          method: 'POST',
          corps: {
            chemin,
            octets,
            session: note.session,
            ...(connu === undefined ? {} : { secret: connu.secret }),
          },
          signal,
        });
        const envoi: EnvoiNote =
          donnees.reprise && connu !== undefined
            ? connu
            : { id: donnees.id, secret: donnees.secret ?? '', recus: [] };
        note.envois[nom] = envoi;
        parId.set(envoi.id, nom);
        ecrireNote(cle, note);
        return { fichierId: envoi.id, mode: donnees.envoi?.mode ?? 'api', dejaRecus: envoi.recus };
      }),
    signer: (fichierId, numeros) =>
      relayer(async () => {
        const { donnees } = await requeteDepotPublic<{
          morceaux: { numero: number; url: string }[];
        }>(jeton, `/fichiers/${encodeURIComponent(fichierId)}/morceaux`, {
          method: 'POST',
          corps: { numeros },
          secret: envoiDe(fichierId).secret,
          signal,
        });
        return donnees.morceaux;
      }),
    async envoyer(cible, corps, progres) {
      const envoi = envoiDe(cible.fichierId);
      const statut = await envoyerMorceauDepotPublic(
        cible.url === null
          ? { jeton, fichierId: cible.fichierId, numero: cible.numero, secret: envoi.secret }
          : { url: cible.url },
        corps,
        progres,
        signal,
      );
      if (statut >= 200 && statut < 300) {
        envoi.recus = [...new Set([...envoi.recus, cible.numero])];
        ecrireNote(cle, note);
        return;
      }
      if (statut === -1) throw new ErreurEnvoiDepot('Envoi interrompu.', true);
      // Réseau coupé, stockage indisponible, ou URL signée périmée (le
      // stockage répond 403) : on réessaie, l'URL se signe de nouveau.
      const passagere = statut === 0 || statut >= 500 || statut === 429 || cible.url !== null;
      throw new ErreurEnvoiDepot(`Envoi refusé (${String(statut)}).`, !passagere);
    },
    terminer: (fichierId) =>
      relayer(async () => {
        await requeteDepotPublic<unknown>(
          jeton,
          `/fichiers/${encodeURIComponent(fichierId)}/terminer`,
          { method: 'POST', secret: envoiDe(fichierId).secret, signal },
        );
        const nom = parId.get(fichierId);
        if (nom !== undefined) delete note.envois[nom];
        ecrireNote(cle, note);
      }),
  };
}
